import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {CombinationAlarmParameters} from '../../../../../../../shared/classes/alarms/alarm-parameters/combination-alarm-parameters';
import {FormArray, FormBuilder, FormControl, Validators} from '@angular/forms';
import {AlarmParameterDefaultForm} from '../../../../../../../shared/classes/alarms/alarm-parameters/alarm-parameter-default-form';
import {ProjectService} from '../../../../../../../shared/services/project/project.service';
import {Subject, Subscription} from 'rxjs';
import {Alarm} from '../../../../../../../shared/classes/alarms/alarm';

@Component({
    selector: 'app-alarm-parameters-form-combination-alarm',
    templateUrl: './alarm-parameters-form-combination-alarm.component.html',
    styleUrls: ['./alarm-parameters-form-combination-alarm.component.css']
})
export class AlarmParametersFormCombinationAlarmComponent extends AlarmParameterDefaultForm implements OnInit, OnDestroy {

    params: CombinationAlarmParameters;
    projectAlarms: any[];
    expressionElementsForm: FormArray;
    operators = [new Operator('and'), new Operator('xor'), new Operator('or')];
    displayForm = true;
    displayNewInputComponent = true;
    availableAlarms = [];
    operationsFormChangesSubsc: Subscription;
    paramsChangeSubsc: Subscription;
    submittedSubsc: Subscription;
    noProjectAlarm: boolean;
    hasBeenSubmitted: boolean;
    focusNewInputElement = new Subject();
    @Output() disableAlarmCreation = new EventEmitter();
    @ViewChild('newElementInput') newElementInput: ElementRef;
    private unAllowedAlarmTypes = ['threshold-alarm', 'liris-location-alarm', 'wimd'];

    get enabledType(): 'alarm' | 'operator' {
        if (!this.expressionElementsForm) {
            return undefined;
        }
        const focusedControl: any = this.expressionElementsForm.controls.find(
            (control: any) => control.isFocused
        );
        if (focusedControl) {
            return focusedControl.elementType;
        } else {
            if (this.expressionElementsForm.controls.length === 0) {
                return 'alarm';
            }
            const lastElementType =
                (<any>this.expressionElementsForm.controls[this.expressionElementsForm.controls.length - 1]).elementType;
            return lastElementType === 'alarm' ? 'operator' : 'alarm';
        }
    }

    get editedControl(): any {
        if (!this.expressionElementsForm) {
            return undefined;
        }
        return this.expressionElementsForm.controls.find(
            (control: any) => control.isFocused
        );
    }

    constructor(protected formBuilder: FormBuilder,
                protected projectService: ProjectService,
                protected cd: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        super.ngOnInit();
        this.expressionElementsForm = this.getEmptyOperationForm();
        this.projectService.getProject(this.projectId, 'alarms').subscribe(
            (project: any) => {
                if (!project.alarms || 0 === project.alarms.length) {
                    this._disableAlarmeCreation();
                    return;
                }
                this.projectAlarms = this.filterProjectAlarms(project.alarms);
                if (0 === this.projectAlarms.length) {
                    this._disableAlarmeCreation();
                    return;
                }
                this.resetExpressionElementForm(this.params ? this.params.expression : undefined);
            }
        );
        this.operationsFormChangesSubsc = this.subscribeToOperationsFormChanges();
        this.paramsChangeSubsc = this.paramsChange.subscribe(
            (changes: SimpleChanges) => {
                if (changes.params) {
                    this.resetExpressionElementForm(changes.params.currentValue.expression);
                    this.form.get('message').setValue(changes.params.currentValue.message);
                }
            });
        this.submittedSubsc = this.submitted.subscribe(
            () => {
                this.hasBeenSubmitted = true;
                this.updateMainForm();
            }
        );
    }

    ngOnDestroy() {
        if (this.operationsFormChangesSubsc) {
            this.operationsFormChangesSubsc.unsubscribe();
        }
        this.paramsChangeSubsc.unsubscribe();
        this.submittedSubsc.unsubscribe();
    }

    mapAlarmParamsToForm() {
        this.params = this.params ? this.params : new CombinationAlarmParameters();
        this.form = this.formBuilder.group({
                message: [{value: this.params.message, disabled: this.readOnly}, [Validators.required, Validators.minLength(5)]],
                expression: [{value: this.params.message, disabled: this.readOnly}, Validators.required]
            }
        );
    }

    addElement(element: Alarm | Operator, control: any = this.editedControl) {
        if (control) {
            control.setValue(element);
        } else {
            this.reinitNewInputComponent();
            this.pushNewControlToOperationForm(element, this.enabledType);
        }
    }

    addElementFromComponentExternalSource(element: Alarm | Operator) {
        if (this.editedControl) {
            this.editedControl.valueExternalChanges.next(element);
        }
        this.addElement(element);
    }

    unfocusOtherElements(index?) {
        this.expressionElementsForm.controls.forEach(
            (control: any, i) => {
                if (i !== index) {
                    control.valueExternalChanges.next(null);
                    control.isFocused = false;
                }
            }
        );
    }

    deleteElement(index) {
        this.displayForm = false;
        this.cd.detectChanges();
        this.expressionElementsForm.removeAt(index);
        this.unfocusOtherElements();
        this.displayForm = true;
    }

    resetExpressionElementForm(expression?) {
        this.displayForm = false;
        this.cd.detectChanges();
        if (this.operationsFormChangesSubsc) {
            this.operationsFormChangesSubsc.unsubscribe();
        }
        this.initNewExpressionElementForm(expression);
        this.displayForm = true;
    }

    checkElementHasNoCombinationError(control) {
        return !control.hasError('invalid combination: first element')
            && !control.hasError('invalid combination: second element');
    }

    private initNewExpressionElementForm(expression?) {
        this.expressionElementsForm = this.getEmptyOperationForm();
        if (expression) {
            this.parseExpressionToExpressionElementsForm();
        }
        this.callbacksAfterOperationFormValueChange();
        this.subscribeToOperationsFormChanges();
    }

    private reinitNewInputComponent() {
        this.displayNewInputComponent = false;
        this.cd.detectChanges();
        this.displayNewInputComponent = true;
    }

    private pushNewControlToOperationForm(controlValue, elementType: 'alarm' | 'operator') {

        const validator_wrongCombination_firstElement = (control) => {
            const currentIndex = this.expressionElementsForm.controls.findIndex(
                (_control) => _control === control
            );
            const nextControl: any = this.expressionElementsForm.at(currentIndex + 1);
            if (nextControl) {
                return control.elementType === nextControl.elementType ? {'invalid combination: first element': true} : null;
            } else {
                return null;
            }
        };

        const validator_wrongCombination_secondElement = (control) => {
            const currentIndex = this.expressionElementsForm.controls.findIndex(
                (_control) => _control === control
            );
            const nextControl: any = this.expressionElementsForm.at(currentIndex - 1);
            if (nextControl) {
                return control.elementType === nextControl.elementType ? {'invalid combination: second element': true} : null;
            } else {
                return null;
            }
        };

        const newControl: any = new FormControl(controlValue,
            [Validators.required,
                (control) => typeof control.value === 'string' ? {'incorrect value': true} : null,
                (control) => typeof control.value === 'string' ? {'incorrect value': true} : null,
                validator_wrongCombination_firstElement,
                validator_wrongCombination_secondElement
            ]);
        newControl.elementType = elementType;
        newControl.valueExternalChanges = new Subject();
        newControl.isFocused = false;
        this.expressionElementsForm.push(newControl);
    }

    private filterProjectAlarms(alarms: any[]) {
        return alarms.filter(
            (alarm) => {
                if ('static-threshold-alarm' === alarm.type) { return true; }
                return alarm.active
                    && parseInt(alarm.id, 10) !== parseInt(this.alarmId, 10)
                    && !this.unAllowedAlarmTypes.find(
                    (type) => alarm.type.includes(type)
                );
            }
        );
    }

    private parseExpressionToExpressionElementsForm() {
        this.expressionElementsForm = this.getEmptyOperationForm();
        this.params.expression
            .split('%')
            .filter((fragment) => fragment.length > 0)
            .forEach(
                (fragment: string) => {
                    const fragmentArray = fragment.trim().split('alarm_');
                    if (fragmentArray.length > 1) {
                        const alarm = this.projectAlarms.find(
                            (al: any) => parseInt(al.id, 10) === parseInt(fragmentArray[1], 10)
                        );
                        this.pushNewControlToOperationForm(alarm, 'alarm');
                    } else {
                        this.pushNewControlToOperationForm(new Operator(fragment), 'operator');
                    }
                }
            );
    }

    private subscribeToOperationsFormChanges() {
        return this.expressionElementsForm.valueChanges.subscribe(
            () => this.callbacksAfterOperationFormValueChange()
        );
    }

    private callbacksAfterOperationFormValueChange() {
        this.updateMainForm();
        this.updateAvailableAlarmsList();
    }

    private updateMainForm() {
        if (this.expressionElementsForm.invalid || !this.expressionElementsForm.controls) {
            this.form.controls.expression.setValue(undefined);
            return;
        }
        const expression = this.expressionElementsForm.controls.reduce(
            (expr, control: any) => {
                return expr + (control.elementType === 'alarm' ? `%alarm_${control.value.id}%`
                    : ` ${control.value.name ? control.value.name.trim() : ''} `);
            }, ''
        );
        this.form.controls.expression.setValue(expression);
    }

    private updateAvailableAlarmsList() {
        const selectedAlarms = this.expressionElementsForm.controls
            .reduce(
                (_selectedAlarms, control: any) => {
                    if (control && 'alarm' === control.elementType && control.value) {
                        _selectedAlarms.push(control);
                    }
                    return _selectedAlarms;
                }, []);

        this.availableAlarms = this.projectAlarms
            .reduce(
                (availableAlarms, alarm) => {
                    if (!selectedAlarms.find(
                        (selectedAlarm) => selectedAlarm.value.id === alarm.id
                    )) {
                        availableAlarms.push(alarm);
                    }
                    return availableAlarms;
                }, []
            );
    }

    private getEmptyOperationForm() {
        return new FormArray([]);
    }

    private _disableAlarmeCreation() {
        this.noProjectAlarm = true;
        this.disableAlarmCreation.emit(true);
    }

}

export class Operator {
    constructor(public name: string) {
    }
}
